import { CSSProperties, useCallback } from "react";
import {
  PaymentMethod,
  PaymentMethodLabel,
  PaymentSettings,
  StoreSeller,
} from "../../models";
import { Button } from "../../components/ui/Button";
import { CheckIcon, AlertTriangleIcon } from "../../components/ui/Icons";
import { PaymentMethodIcon } from "../../components/ui/PaymentMethodIcon";
import { SellerPicker } from "../../components/checkout/SellerPicker";
import { useElementHeight } from "../../hooks/useElementHeight";
import { useStickyCtaOffset } from "../../hooks/useStickyCtaOffset";
import { formatCurrency } from "../../utils/currency";
import {
  CheckoutTotals,
  getCheckoutTotals,
  getInstallmentInfo,
} from "../../utils/pricing";
import { CartItem } from "../../models/cart";
import { PlusIcon, TrashIcon } from "./CheckoutIcons";
import { CurrencyInput } from "./CurrencyInput";
import { InstallmentPicker } from "./InstallmentPicker";
import { StepBackButton } from "./StepBackButton";
import { OrderSummaryCard } from "./OrderSummary";
import {
  CHECKOUT_PRIMARY_ACTION_CLASS,
  CHECKOUT_SIDEBAR_GRID_CLASS,
  TOUCH_TARGET_CLASS,
} from "./styles";
import { ALL_PAYMENT_METHODS, PaymentEntry, PaymentMode } from "./types";

const BAR_HEIGHT_VAR = "--checkout-bar-height";

function getMaxInstallments(
  amount: number,
  settings: PaymentSettings | null,
  disabled: boolean,
): number {
  if (disabled || !settings) return 1;
  return getInstallmentInfo(amount, settings)?.count ?? 1;
}

export function PaymentCoverageBar({
  total,
  paid,
}: {
  total: number;
  paid: number;
}) {
  const percentage = total > 0 ? Math.min((paid / total) * 100, 100) : 0;
  const isCovered = Math.abs(total - paid) < 0.01;
  const isOver = paid > total + 0.01;

  const barColor = isCovered
    ? "bg-olivine-dark"
    : isOver
      ? "bg-warning"
      : "bg-chocolate-dark";

  return (
    <div className="mt-3">
      <div className="flex justify-between text-sm mb-1">
        <span className="text-text-medium">
          R$ {formatCurrency(paid)} de R$ {formatCurrency(total)}
        </span>
        <span className="font-bold text-text-dark">
          {Math.round(percentage)}%
        </span>
      </div>
      <div
        role="progressbar"
        aria-label="Valor do pedido coberto"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(percentage)}
        className="w-full h-2.5 bg-champagne-dark/40 rounded-full overflow-hidden"
      >
        <div
          className={`h-full rounded-full transition-all duration-500 ease-out ${barColor}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

interface CoverageStatus {
  totalPaid: number;
  paymentRemainder: number;
  isCovered: boolean;
  isOver: boolean;
}

function CoverageMessage({ coverage }: { coverage: CoverageStatus }) {
  if (coverage.isCovered) {
    return (
      <p className="flex items-center gap-2 mt-3 rounded-lg bg-olivine/20 px-3 py-2 text-sm font-semibold text-text-dark">
        <CheckIcon className="w-5 h-5 text-olivine-dark shrink-0" />
        Valor total coberto
      </p>
    );
  }
  if (coverage.isOver) {
    return (
      <p className="flex items-center gap-2 mt-3 rounded-lg bg-warning/25 px-3 py-2 text-sm font-semibold text-text-dark">
        <AlertTriangleIcon className="w-5 h-5 shrink-0" />
        Valor excede o total em R${" "}
        {formatCurrency(Math.abs(coverage.paymentRemainder))}
      </p>
    );
  }
  if (coverage.paymentRemainder > 0) {
    return (
      <p className="text-sm text-text-medium mt-2">
        Faltam{" "}
        <span className="font-bold text-text-dark">
          R$ {formatCurrency(coverage.paymentRemainder)}
        </span>{" "}
        para cobrir o total
      </p>
    );
  }
  return null;
}

function MethodButton({
  method,
  selected,
  onSelect,
  children,
}: {
  method: PaymentMethod;
  selected: boolean;
  onSelect: () => void;
  children?: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className={[
        `${TOUCH_TARGET_CLASS} border rounded-xl px-3 py-3 flex items-center gap-2.5 transition-all cursor-pointer text-left`,
        selected
          ? "bg-chocolate-dark text-white border-chocolate-dark shadow-md"
          : "bg-white text-text-medium border-champagne-dark hover:border-chocolate hover:text-chocolate-dark hover:bg-champagne/30",
      ].join(" ")}
    >
      <span
        className={[
          "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
          selected ? "bg-white/20" : "bg-champagne",
        ].join(" ")}
      >
        <PaymentMethodIcon method={method} />
      </span>
      <span className="flex flex-col items-start leading-tight">
        <span className={`text-sm ${selected ? "font-bold" : "font-medium"}`}>
          {PaymentMethodLabel[method]}
        </span>
        {children}
      </span>
    </button>
  );
}

function PaymentEntryCard({
  entry,
  index,
  totalEntries,
  availableMethods,
  paymentRemainder,
  paymentSettings,
  hasPromotionalItems,
  onUpdate,
  onRemove,
  onFillRemainder,
}: {
  entry: PaymentEntry;
  index: number;
  totalEntries: number;
  availableMethods: PaymentMethod[];
  paymentRemainder: number;
  paymentSettings: PaymentSettings | null;
  hasPromotionalItems: boolean;
  onUpdate: (
    i: number,
    field: keyof PaymentEntry,
    value: number | PaymentMethod,
  ) => void;
  onRemove: (i: number) => void;
  onFillRemainder: (i: number) => void;
}) {
  const maxInstallments =
    entry.paymentMethod === PaymentMethod.CreditCard
      ? getMaxInstallments(
          entry.amountPaid,
          paymentSettings,
          hasPromotionalItems,
        )
      : 1;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-champagne-dark/60 overflow-hidden animate-slide-in">
      <div className="flex items-center justify-between pl-4 pr-1 py-1 bg-champagne/60 border-b border-champagne-dark/30">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-full bg-chocolate-dark text-white flex items-center justify-center text-sm font-bold">
            {index + 1}
          </span>
          <span className="text-sm font-semibold text-text-dark">
            Pagamento {index + 1}
          </span>
        </div>
        {totalEntries > 1 && (
          <button
            type="button"
            onClick={() => onRemove(index)}
            className={`${TOUCH_TARGET_CLASS} flex items-center justify-center text-text-medium hover:text-danger hover:bg-danger/10 rounded-lg transition-colors`}
            aria-label={`Remover pagamento ${index + 1}`}
          >
            <TrashIcon className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="p-4">
        <p className="text-sm font-medium text-text-medium mb-2">
          Forma de pagamento
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
          {availableMethods.map((pm) => (
            <MethodButton
              key={pm}
              method={pm}
              selected={pm === entry.paymentMethod}
              onSelect={() => onUpdate(index, "paymentMethod", pm)}
            />
          ))}
        </div>

        {maxInstallments > 1 && (
          <div className="mb-4">
            <InstallmentPicker
              amount={entry.amountPaid}
              maxCount={maxInstallments}
              value={entry.installments ?? 1}
              interestFree={
                paymentSettings?.installmentsWithoutInterest ?? false
              }
              onChange={(count) => onUpdate(index, "installments", count)}
            />
          </div>
        )}

        <label
          htmlFor={`payment-amount-${entry.id}`}
          className="block text-sm font-medium text-text-medium mb-2"
        >
          Valor
        </label>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="flex-1 relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-text-medium font-medium">
              R$
            </span>
            <div className="pl-9">
              <CurrencyInput
                id={`payment-amount-${entry.id}`}
                value={entry.amountPaid}
                onChange={(v) => onUpdate(index, "amountPaid", v)}
              />
            </div>
          </div>
          {Math.abs(paymentRemainder) > 0.01 && (
            <button
              type="button"
              onClick={() => onFillRemainder(index)}
              className={`${TOUCH_TARGET_CLASS} text-sm text-chocolate-dark border border-chocolate-dark rounded-xl px-4 py-2.5 whitespace-nowrap hover:bg-champagne transition-colors font-semibold w-full sm:w-auto`}
            >
              Completar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function CombinePaymentSelector({
  selected,
  onSelect,
  pixPreview,
  paymentSettings,
  total,
  hasPromotionalItems,
}: {
  selected: PaymentMethod;
  onSelect: (method: PaymentMethod) => void;
  pixPreview: CheckoutTotals;
  paymentSettings: PaymentSettings | null;
  total: number;
  hasPromotionalItems: boolean;
}) {
  const installmentInfo =
    hasPromotionalItems || !paymentSettings
      ? null
      : getInstallmentInfo(total, paymentSettings);
  const interestFree = paymentSettings?.installmentsWithoutInterest ?? false;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {ALL_PAYMENT_METHODS.map((pm) => {
        const isSelected = pm === selected;
        return (
          <MethodButton
            key={pm}
            method={pm}
            selected={isSelected}
            onSelect={() => onSelect(pm)}
          >
            {pm === PaymentMethod.Pix && pixPreview.pixDiscountApplies && (
              <span
                className={`text-sm font-semibold mt-0.5 ${isSelected ? "text-white" : "text-text-dark"}`}
              >
                R$ {formatCurrency(pixPreview.net)} no Pix
              </span>
            )}
            {pm === PaymentMethod.CreditCard && installmentInfo && (
              <span
                className={`text-sm mt-0.5 ${isSelected ? "text-white" : "text-text-medium"}`}
              >
                até {installmentInfo.count}x de R${" "}
                {formatCurrency(installmentInfo.amount)}
                {interestFree ? " sem juros" : ""}
              </span>
            )}
          </MethodButton>
        );
      })}
    </div>
  );
}

function TotalCard({
  totals,
  items,
}: {
  totals: CheckoutTotals;
  items: CartItem[];
}) {
  return <OrderSummaryCard items={items} totals={totals} />;
}

function CoverageCard({
  totals,
  coverage,
  items,
}: {
  totals: CheckoutTotals;
  coverage: CoverageStatus;
  items: CartItem[];
}) {
  return (
    <OrderSummaryCard items={items} totals={totals}>
      <PaymentCoverageBar total={totals.net} paid={coverage.totalPaid} />
      <CoverageMessage coverage={coverage} />
    </OrderSummaryCard>
  );
}

function CompactCoverage({ coverage }: { coverage: CoverageStatus }) {
  if (coverage.isCovered) return null;
  const amount = formatCurrency(Math.abs(coverage.paymentRemainder));
  return (
    <p
      role="status"
      className={`text-sm font-semibold leading-tight ${coverage.isOver ? "text-danger" : "text-text-dark"}`}
    >
      {coverage.isOver ? `Excede R$ ${amount}` : `Faltam R$ ${amount}`}
    </p>
  );
}

function MobileOrderBar({
  totals,
  coverage,
  barRef,
  onNext,
  canProceed,
}: {
  totals: CheckoutTotals;
  coverage?: CoverageStatus;
  barRef: (node: HTMLDivElement | null) => void;
  onNext: () => void;
  canProceed: boolean;
}) {
  const stickyCtaRef = useStickyCtaOffset<HTMLDivElement>();
  const ref = useCallback(
    (node: HTMLDivElement | null) => {
      barRef(node);
      stickyCtaRef(node);
    },
    [barRef, stickyCtaRef]
  );

  return (
    <div
      ref={ref}
      className="fixed inset-x-0 bottom-consent z-sticky-cta lg:hidden bg-white border-t border-champagne-dark shadow-[0_-8px_20px_-14px_rgba(60,36,21,0.4)] px-4 pt-3 pb-safe"
    >
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0 leading-tight">
          <p className="text-xs text-text-medium">Total dos produtos</p>
          <p className="text-lg font-bold text-text-dark">
            R$ {formatCurrency(totals.net)}
          </p>
          {coverage && <CompactCoverage coverage={coverage} />}
        </div>
        <Button
          onClick={onNext}
          disabled={!canProceed}
          size="lg"
          className={`flex-1 min-h-[48px] ${CHECKOUT_PRIMARY_ACTION_CLASS}`}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}

interface PaymentStepProps {
  sellers: StoreSeller[];
  loadingSellers: boolean;
  selectedSeller: StoreSeller | null;
  onSelectSeller: (seller: StoreSeller) => void;
  items: CartItem[];
  totals: CheckoutTotals;
  paymentEntries: PaymentEntry[];
  paymentRemainder: number;
  paymentMode: PaymentMode;
  hasPromotionalItems: boolean;
  paymentSettings: PaymentSettings | null;
  onSelectCombineMethod: (method: PaymentMethod) => void;
  onSwitchToDetailed: () => void;
  onSwitchToCombine: () => void;
  onAddPaymentMethod: () => void;
  onRemove: (i: number) => void;
  onUpdate: (
    i: number,
    field: keyof PaymentEntry,
    value: number | PaymentMethod,
  ) => void;
  onFillRemainder: (i: number) => void;
  onNext: () => void;
  backLabel: string;
  onBack: () => void;
  isValid: boolean;
}

export function PaymentStep({
  sellers,
  loadingSellers,
  selectedSeller,
  onSelectSeller,
  items,
  totals,
  paymentEntries,
  paymentRemainder,
  paymentMode,
  hasPromotionalItems,
  paymentSettings,
  onSelectCombineMethod,
  onSwitchToDetailed,
  onSwitchToCombine,
  onAddPaymentMethod,
  onRemove,
  onUpdate,
  onFillRemainder,
  onNext,
  isValid,
  backLabel,
  onBack,
}: PaymentStepProps) {
  const { ref: barRef, height: barHeight } = useElementHeight<HTMLDivElement>();
  const canProceed = isValid && !!selectedSeller;
  const contentStyle = { [BAR_HEIGHT_VAR]: `${barHeight}px` } as CSSProperties;
  const contentClass =
    "pb-[calc(var(--checkout-bar-height)+var(--consent-offset,0px)+1.5rem)] lg:pb-0";

  if (paymentMode === "combine") {
    const selectedEntry = paymentEntries[0];
    const selectedMethod = selectedEntry?.paymentMethod ?? PaymentMethod.Pix;
    const pixPreview = getCheckoutTotals(
      items,
      [{ paymentMethod: PaymentMethod.Pix }],
      paymentSettings,
    );
    const maxInstallments =
      selectedMethod === PaymentMethod.CreditCard && selectedEntry
        ? getMaxInstallments(
            selectedEntry.amountPaid,
            paymentSettings,
            hasPromotionalItems,
          )
        : 1;

    return (
      <>
        <div className={CHECKOUT_SIDEBAR_GRID_CLASS}>
          <div className={contentClass} style={contentStyle}>
            <SellerPicker
              sellers={sellers}
              loading={loadingSellers}
              selectedSeller={selectedSeller}
              onSelect={onSelectSeller}
            />

            <h2 className="text-sm font-bold text-chocolate-dark mb-2">
              Forma de pagamento
            </h2>
            <CombinePaymentSelector
              selected={selectedMethod}
              onSelect={onSelectCombineMethod}
              pixPreview={pixPreview}
              paymentSettings={paymentSettings}
              total={totals.gross}
              hasPromotionalItems={hasPromotionalItems}
            />

            {maxInstallments > 1 && selectedEntry && (
              <div className="mt-4">
                <InstallmentPicker
                  amount={selectedEntry.amountPaid}
                  maxCount={maxInstallments}
                  value={selectedEntry.installments ?? 1}
                  interestFree={
                    paymentSettings?.installmentsWithoutInterest ?? false
                  }
                  onChange={(count) => onUpdate(0, "installments", count)}
                />
              </div>
            )}

            <button
              type="button"
              onClick={onSwitchToDetailed}
              className="min-h-[44px] mt-3 mb-2 inline-flex items-center gap-1.5 text-sm font-medium text-chocolate-dark hover:text-text-dark transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Dividir em mais de uma forma de pagamento
            </button>

            <div className="hidden lg:flex flex-col gap-1">
              <Button
                onClick={onNext}
                disabled={!canProceed}
                className={`w-full ${CHECKOUT_PRIMARY_ACTION_CLASS}`}
              >
                Continuar
              </Button>
              <StepBackButton label={backLabel} onClick={onBack} />
            </div>
          </div>

          <div className="hidden lg:block lg:sticky lg:top-20">
            <TotalCard totals={totals} items={items} />
          </div>
        </div>

        <MobileOrderBar
          totals={totals}
          barRef={barRef}
          onNext={onNext}
          canProceed={canProceed}
        />
      </>
    );
  }

  const usedMethods = new Set(paymentEntries.map((e) => e.paymentMethod));
  const canAddMore = usedMethods.size < ALL_PAYMENT_METHODS.length;
  const totalPaid = paymentEntries.reduce((sum, e) => sum + e.amountPaid, 0);
  const coverage: CoverageStatus = {
    totalPaid,
    paymentRemainder,
    isCovered: Math.abs(paymentRemainder) < 0.01,
    isOver: paymentRemainder < -0.01,
  };

  return (
    <>
      <div className={CHECKOUT_SIDEBAR_GRID_CLASS}>
        <div className={contentClass} style={contentStyle}>
          <SellerPicker
            sellers={sellers}
            loading={loadingSellers}
            selectedSeller={selectedSeller}
            onSelect={onSelectSeller}
          />

          <div className="mb-3">
            <h2 className="text-sm font-bold text-chocolate-dark">
              Divida o valor do pedido
            </h2>
            <p className="text-sm text-text-medium mt-0.5">
              Combine Pix e cartão para cobrir o total.
            </p>
            <div className="lg:hidden">
              <PaymentCoverageBar total={totals.net} paid={coverage.totalPaid} />
              <CoverageMessage coverage={coverage} />
            </div>
          </div>

          <div className="flex flex-col gap-3 mb-4">
            {paymentEntries.map((entry, idx) => {
              const availableMethods = ALL_PAYMENT_METHODS.filter(
                (pm) => pm === entry.paymentMethod || !usedMethods.has(pm),
              );
              return (
                <PaymentEntryCard
                  key={entry.id}
                  entry={entry}
                  index={idx}
                  totalEntries={paymentEntries.length}
                  availableMethods={availableMethods}
                  paymentRemainder={paymentRemainder}
                  paymentSettings={paymentSettings}
                  hasPromotionalItems={hasPromotionalItems}
                  onUpdate={onUpdate}
                  onRemove={onRemove}
                  onFillRemainder={onFillRemainder}
                />
              );
            })}
          </div>

          {canAddMore && (
            <button
              type="button"
              onClick={onAddPaymentMethod}
              className="w-full min-h-[44px] border-2 border-dashed border-chocolate-dark/40 rounded-xl py-5 px-4 flex flex-col items-center justify-center gap-2 text-chocolate-dark hover:border-chocolate-dark hover:bg-champagne/50 transition-all mb-4 group"
            >
              <span className="w-10 h-10 rounded-full bg-champagne group-hover:bg-chocolate-dark group-hover:text-white text-chocolate-dark flex items-center justify-center transition-all duration-200">
                <PlusIcon className="w-5 h-5" />
              </span>
              <span className="text-sm font-semibold">
                Adicionar outra forma de pagamento
              </span>
              <span className="text-sm text-text-medium">
                Divida o valor entre diferentes meios
              </span>
            </button>
          )}

          <div className="text-center mb-4">
            <button
              type="button"
              onClick={onSwitchToCombine}
              className="min-h-[44px] text-sm font-medium text-chocolate-dark underline hover:text-text-dark transition-colors"
            >
              Voltar para pagamento simples
            </button>
          </div>

          <div className="hidden lg:flex flex-col gap-1">
            <Button
              onClick={onNext}
              disabled={!canProceed}
              className={`w-full ${CHECKOUT_PRIMARY_ACTION_CLASS}`}
            >
              Continuar
            </Button>
            <StepBackButton label={backLabel} onClick={onBack} />
          </div>
        </div>

        <div className="hidden lg:block lg:sticky lg:top-20">
          <CoverageCard totals={totals} coverage={coverage} items={items} />
        </div>
      </div>

      <MobileOrderBar
        totals={totals}
        coverage={coverage}
        barRef={barRef}
        onNext={onNext}
        canProceed={canProceed}
      />
    </>
  );
}
